import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  uploadVehicleDocument,
  uploadVehiclePhoto,
  fetchVehicleCompliance,
  verifyVehicleFromRegistry,
  fetchVehicleFileForPreview,
  downloadVehicleFile,
} from "../api/ownerCarsApi";
import { toast } from "../lib/toast";

/* ----------------------------- constants ----------------------------- */

const DOC_TYPES = {
  rc: "RC",
  insurance: "INSURANCE",
  permit: "PERMIT",
  fitness: "FITNESS",
  puc: "PUC",
};

const PHOTO_SLOTS = {
  front: "FRONT",
  back: "BACK",
};

/* ----------------------------- helpers ----------------------------- */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function toMapByTypeMaybe(raw, typeKeyCandidates = ["docType", "type", "photoType"]) {
  if (!raw) return {};

  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }

  if (Array.isArray(raw)) {
    const out = {};
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      let key = null;
      for (const cand of typeKeyCandidates) {
        if (item[cand]) {
          key = String(item[cand]);
          break;
        }
      }
      if (key) out[key] = item;
    }
    return out;
  }

  return {};
}

function normalizeCompliance(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      docs: {},
      photos: {},
      updatedAt: null,
      raw: raw || null,
    };
  }

  const docsRaw = raw.docs ?? raw.documents ?? raw.vehicleDocuments ?? null;
  const photosRaw = raw.photos ?? raw.vehiclePhotos ?? null;

  return {
    docs: toMapByTypeMaybe(docsRaw, ["docType", "type"]),
    photos: toMapByTypeMaybe(photosRaw, ["photoType", "type"]),
    updatedAt: raw.updatedAt || raw.lastUpdatedAt || raw.lastCheckedAt || null,
    raw,
  };
}

function extractErrorMessage(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    return data.message || data.error || data.detail || "Request failed";
  }
  return err?.message || "We couldn't complete this request. Please try again.";
}

function fmtDate(d) {
  if (!d) return "";
  try {
    const x = new Date(d);
    if (!Number.isFinite(x.getTime())) return String(d);
    return x.toLocaleDateString();
  } catch {
    return String(d);
  }
}

function guessKindFromMimeOrName(mime, nameOrUrl) {
  const ct = String(mime || "").toLowerCase();
  if (ct.includes("pdf")) return "pdf";
  if (ct.startsWith("image/")) return "image";

  const x = String(nameOrUrl || "").toLowerCase();
  if (x.includes(".pdf")) return "pdf";
  if (
    x.includes(".png") ||
    x.includes(".jpg") ||
    x.includes(".jpeg") ||
    x.includes(".webp") ||
    x.includes(".heic")
  ) {
    return "image";
  }
  return "other";
}

function hasExistingFile(item) {
  return Boolean(item?.fileUrl);
}

function stateKey(item) {
  return String(item?.state || "").toUpperCase();
}

function isApprovedDoc(item) {
  return stateKey(item) === "VERIFIED";
}

function isAutoValid(item) {
  return stateKey(item) === "AUTO_VALID";
}

function isRejectedLike(item) {
  const s = stateKey(item);
  return s === "ACTION_REQUIRED" || s === "REJECTED" || s === "EXPIRED" || s === "INVALID";
}

function prettyState(state) {
  const s = String(state || "").toUpperCase();
  if (!s) return "Unknown";
  if (s === "AUTO_VALID") return "Not required";
  if (s === "ACTION_REQUIRED") return "Action required";
  return s.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
}

function getStateTone(item) {
  if (isApprovedDoc(item)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (isAutoValid(item)) {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  if (isRejectedLike(item)) {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (hasExistingFile(item)) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function getValidityTone(item) {
  if (!item?.validTill) return "border-slate-200 bg-slate-50 text-slate-600";
  if (item?.expired) return "border-rose-200 bg-rose-50 text-rose-700";
  if (item?.expiringSoon) return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function buildPrimaryInfoChips(item) {
  if (!item) return [];

  const chips = [
    {
      label: prettyState(item.state),
      tone: getStateTone(item),
    },
  ];

  if (item.validTill) {
    chips.push({
      label: item.expired
        ? `Expired ${fmtDate(item.validTill)}`
        : `Valid till ${fmtDate(item.validTill)}`,
      tone: getValidityTone(item),
    });
  }

  return chips;
}

function buildDetailCells(item) {
  if (!item) return [];

  const cells = [];

  if (item.docNumber) cells.push({ label: "Number", value: item.docNumber });
  if (item.companyName) cells.push({ label: "Provider", value: item.companyName });
  if (item.permitType) cells.push({ label: "Permit type", value: item.permitType });
  if (item.issuingAuthority) cells.push({ label: "Authority", value: item.issuingAuthority });
  if (item.validFrom) cells.push({ label: "Valid from", value: fmtDate(item.validFrom) });
  if (item.vehicleClass) cells.push({ label: "Class", value: item.vehicleClass });
  if (item.policyType) cells.push({ label: "Policy", value: item.policyType });

  return cells;
}

function buildIssueChips(item) {
  if (!item) return [];

  const chips = [];

  if (item.issueMessage && !isApprovedDoc(item) && !isAutoValid(item)) {
    chips.push(item.issueMessage);
  }
  if (item.expired) chips.push("Expired document");
  if (item.expiringSoon) chips.push("Expiring soon");

  return Array.from(new Set(chips.filter(Boolean)));
}

function getOverallComplianceMeta(compliance) {
  const raw = compliance?.raw || {};
  const verified = raw?.complianceVerified === true;
  const complete = raw?.complianceComplete === true;
  const status = String(raw?.status || "").toUpperCase();

  if (verified && complete) {
    return {
      label: "Verified",
      hint: "All required items are approved.",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-800",
    };
  }

  if (complete && !verified) {
    return {
      label: "Under review",
      hint: raw?.message || "Verification is in progress.",
      tone: "border-amber-200 bg-amber-50 text-amber-900",
    };
  }

  return {
    label: status === "ACTION_REQUIRED" ? "Action required" : "Documents required",
    hint: raw?.message || "Upload missing items and complete verification.",
    tone: "border-rose-200 bg-rose-50 text-rose-700",
  };
}

function isIndependentNonCarVehicle(vehicle) {
  const haystack = String(
    [vehicle?.type, vehicle?.brand, vehicle?.model].filter(Boolean).join(" ")
  ).toLowerCase();

  return (
    haystack.includes("auto") ||
    haystack.includes("rickshaw") ||
    haystack.includes("rikshaw") ||
    haystack.includes("erickshaw")
  );
}

function isERickshawVehicle(vehicle) {
  const haystack = String(
    [vehicle?.type, vehicle?.brand, vehicle?.model].filter(Boolean).join(" ")
  ).toLowerCase();

  return (
    haystack.includes("rickshaw") ||
    haystack.includes("rikshaw") ||
    haystack.includes("erickshaw")
  );
}

function vehicleAgeYearsFromVehicle(vehicle) {
  const candidates = [
    vehicle?.yearOfPurchase,
    vehicle?.purchaseYear,
    vehicle?.year,
    vehicle?.manufactureYear,
    vehicle?.modelYear,
  ];

  for (const value of candidates) {
    const year = Number(value);
    if (Number.isFinite(year) && year >= 1900 && year <= 3000) {
      return new Date().getFullYear() - year;
    }
  }

  return null;
}

function canTriggerVerification(compliance, options) {
  const docs = compliance?.docs || {};
  const photos = compliance?.photos || {};
  const has = (x) => Boolean(x?.fileUrl);

  return (
    has(photos.FRONT) &&
    has(photos.BACK) &&
    has(docs.RC) &&
    has(docs.INSURANCE) &&
    (!options.requiresPermit || has(docs.PERMIT)) &&
    (!options.requiresFitness || has(docs.FITNESS)) &&
    (!options.requiresPuc || has(docs.PUC))
  );
}

async function runLimited(tasks, concurrency = 3) {
  const results = new Array(tasks.length);
  let cursor = 0;

  async function worker() {
    while (cursor < tasks.length) {
      const current = cursor++;
      try {
        results[current] = { status: "fulfilled", value: await tasks[current]() };
      } catch (error) {
        results[current] = { status: "rejected", reason: error };
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

/* ----------------------------- UI ----------------------------- */

function ActionButton({
  variant = "secondary",
  disabled = false,
  onClick,
  type = "button",
  children,
  className = "",
}) {
  const base =
    "inline-flex h-10 items-center justify-center rounded-md border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

  const variants = {
    primary:
      "border-slate-900 bg-slate-900 text-white hover:bg-slate-800 hover:border-slate-800 focus-visible:ring-slate-900",
    secondary:
      "border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50 focus-visible:ring-slate-500",
    ghost:
      "border-transparent bg-transparent text-slate-600 hover:bg-slate-100 focus-visible:ring-slate-400",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classNames(base, variants[variant], className)}
    >
      {children}
    </button>
  );
}

function Chip({ tone = "border-slate-200 bg-slate-50 text-slate-700", children }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium",
        tone
      )}
    >
      {children}
    </span>
  );
}

function MutedChip({ children }) {
  return (
    <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
      {children}
    </span>
  );
}

function DetailCell({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-slate-800 break-words">{value}</div>
    </div>
  );
}

function SimpleSection({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
        ) : null}
      </div>
      <div>{children}</div>
    </section>
  );
}

function DocumentRow({
  title,
  hint,
  accept,
  disabled,
  selectedFile,
  existingItem,
  onPick,
  onClear,
  onView,
  uploadText = "Upload",
  isPhoto = false,
}) {
  const inputRef = useRef(null);
  const hasFile = hasExistingFile(existingItem);
  const primaryChips = buildPrimaryInfoChips(existingItem);
  const detailCells = buildDetailCells(existingItem);
  const issueChips = buildIssueChips(existingItem);

  const isVerifiedLocked = hasFile && (isApprovedDoc(existingItem) || isAutoValid(existingItem));
  const isExplicitlyEditable = existingItem?.reuploadAllowed === true;
  const isMissing = !hasFile;
  const canReplace = !selectedFile && !disabled && (isMissing || isExplicitlyEditable) && !isVerifiedLocked;

  const showView = hasFile;
  const showReplace = canReplace;
  const showChange = Boolean(selectedFile);

  return (
    <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold text-slate-950">{title}</div>
          {!hasFile && !selectedFile ? <MutedChip>Pending</MutedChip> : null}
          {selectedFile ? <MutedChip>Selected</MutedChip> : null}
          {primaryChips.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {primaryChips.map((chip, idx) => (
                <Chip key={`${title}-primary-${idx}`} tone={chip.tone}>
                  {chip.label}
                </Chip>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-2 text-sm text-slate-600">{hint}</div>

        {!isPhoto && detailCells.length > 0 ? (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {detailCells.map((cell, idx) => (
              <DetailCell
                key={`${title}-detail-${idx}`}
                label={cell.label}
                value={cell.value}
              />
            ))}
          </div>
        ) : null}

        {issueChips.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {issueChips.map((issue, idx) => (
              <Chip
                key={`${title}-issue-${idx}`}
                tone="border-rose-200 bg-rose-50 text-rose-700"
              >
                {issue}
              </Chip>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap gap-2 md:justify-end">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onPick}
          disabled={disabled}
          className="hidden"
        />

        {showView ? (
          <ActionButton variant="secondary" disabled={disabled} onClick={onView}>
            View
          </ActionButton>
        ) : null}

        {showReplace ? (
          <ActionButton
            variant="secondary"
            disabled={disabled}
            onClick={() => inputRef.current?.click?.()}
          >
            {hasFile ? "Replace" : uploadText}
          </ActionButton>
        ) : null}

        {showChange ? (
          <>
            <ActionButton
              variant="secondary"
              disabled={disabled}
              onClick={() => inputRef.current?.click?.()}
            >
              Change
            </ActionButton>
            <ActionButton variant="ghost" disabled={disabled} onClick={onClear}>
              Clear
            </ActionButton>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ListDivider() {
  return <div className="border-t border-slate-200" />;
}

function DocumentViewerModal({
  open,
  onClose,
  title,
  objectUrl,
  fileKind,
  busy,
  onDownload,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isImage = fileKind === "image";
  const isPdf = fileKind === "pdf";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50"
      />

      <div className="relative w-full max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="min-w-0 truncate text-sm font-medium text-slate-950">
            {title}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <ActionButton
              variant="secondary"
              onClick={onDownload}
              disabled={busy || !objectUrl}
            >
              Download
            </ActionButton>
            <ActionButton variant="ghost" onClick={onClose}>
              Close
            </ActionButton>
          </div>
        </div>

        <div className="h-[78vh] bg-slate-100">
          {busy ? (
            <div className="grid h-full place-items-center text-sm text-slate-600">
              Loading file…
            </div>
          ) : !objectUrl ? (
            <div className="grid h-full place-items-center text-sm text-slate-600">
              Preview unavailable
            </div>
          ) : isImage ? (
            <div className="flex h-full items-center justify-center p-5">
              <img
                src={objectUrl}
                alt={title || "Preview"}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : isPdf ? (
            <iframe
              title={title || "PDF Preview"}
              src={objectUrl}
              className="h-full w-full border-0"
            />
          ) : (
            <div className="grid h-full place-items-center p-6 text-center text-sm text-slate-600">
              This file type cannot be previewed here. Use download instead.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- page ----------------------------- */

export default function OwnerVehicleDocuments() {
  const { registrationNumber: regFromParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const vehicle = location.state?.vehicle || null;
  const registrationNumber = String(
    regFromParams || vehicle?.registrationNumber || ""
  ).trim();

  const hasReg = !!registrationNumber;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [compliance, setCompliance] = useState(normalizeCompliance(null));

  const [frontPhoto, setFrontPhoto] = useState(null);
  const [backPhoto, setBackPhoto] = useState(null);

  const [rcFile, setRcFile] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [fitnessFile, setFitnessFile] = useState(null);
  const [pucFile, setPucFile] = useState(null);

  const previewUrlRef = useRef(null);

  const [viewer, setViewer] = useState({
    open: false,
    busy: false,
    title: "",
    objectUrl: "",
    fileKind: "other",
    fileUrl: "",
    filename: "",
  });

  const serverDoc = useCallback(
    (type) => compliance?.docs?.[type] || null,
    [compliance]
  );

  const serverPhoto = useCallback(
    (type) => compliance?.photos?.[type] || null,
    [compliance]
  );

  const isIndependentNonCar = useMemo(() => isIndependentNonCarVehicle(vehicle), [vehicle]);
  const isERickshaw = useMemo(() => isERickshawVehicle(vehicle), [vehicle]);
  const purchaseYear = useMemo(() => {
    const candidates = [
      vehicle?.yearOfPurchase,
      vehicle?.purchaseYear,
      vehicle?.year,
      vehicle?.manufactureYear,
      vehicle?.modelYear,
    ];
    for (const value of candidates) {
      const year = Number(value);
      if (Number.isFinite(year) && year >= 1900 && year <= 3000) return year;
    }
    return null;
  }, [vehicle]);
  const vehicleAgeYears = useMemo(() => vehicleAgeYearsFromVehicle(vehicle), [vehicle]);
  const isVan = useMemo(() => {
    const value = String(vehicle?.type || vehicle?.brand || vehicle?.model || "").toLowerCase();
    return value.includes("van") || value.includes("traveller");
  }, [vehicle]);
  const requiresPermit = useMemo(() => !isERickshaw, [isERickshaw]);
  const requiresFitness = useMemo(() => {
    if (isVan) return false;
    if (!isIndependentNonCar) return true;
    return vehicleAgeYears == null || vehicleAgeYears > 2;
  }, [isIndependentNonCar, isVan, vehicleAgeYears]);
  const requiresPuc = useMemo(() => {
    if (isVan) return false;
    if (isIndependentNonCar) return false;
    return vehicleAgeYears == null || vehicleAgeYears >= 1;
  }, [isIndependentNonCar, isVan, vehicleAgeYears]);

const photoRows = useMemo(
  () => [
    {
      key: "frontPhoto",
      type: PHOTO_SLOTS.front,
      title: "Front photo",
      hint: "Front-side exterior photo with number plate visible",
      selectedFile: frontPhoto,
      setSelectedFile: setFrontPhoto,
    },
    {
      key: "backPhoto",
      type: PHOTO_SLOTS.back,
      title: "Back photo",
      hint: "Back-side exterior photo with number plate visible",
      selectedFile: backPhoto,
      setSelectedFile: setBackPhoto,
    },
  ],
  [frontPhoto, backPhoto]
);

 const docRows = useMemo(
  () => [
    {
      key: "rc",
      type: DOC_TYPES.rc,
      title: "RC",
      hint: "Vehicle registration certificate",
      selectedFile: rcFile,
      setSelectedFile: setRcFile,
    },
    {
      key: "insurance",
      type: DOC_TYPES.insurance,
      title: "Insurance",
      hint: "Commercial insurance document",
      selectedFile: insuranceFile,
      setSelectedFile: setInsuranceFile,
    },
    ...(requiresPermit ? [{
      key: "permit",
      type: DOC_TYPES.permit,
      title: "Permit",
      hint: "Commercial permit document",
      selectedFile: permitFile,
      setSelectedFile: setPermitFile,
    }] : []),
    ...(requiresFitness ? [{
      key: "fitness",
      type: DOC_TYPES.fitness,
      title: "Fitness",
      hint: "Fitness certificate",
      selectedFile: fitnessFile,
      setSelectedFile: setFitnessFile,
    }] : []),
    ...(!isIndependentNonCar ? [{
      key: "puc",
      type: DOC_TYPES.puc,
      title: "PUC",
      hint: !requiresPuc
        ? "Pollution certificate · optional for vehicles less than 1 year old"
        : "Pollution certificate",
      selectedFile: pucFile,
      setSelectedFile: setPucFile,
    }] : []),
  ],
  [rcFile, insuranceFile, permitFile, fitnessFile, pucFile, requiresPermit, requiresFitness, isIndependentNonCar, requiresPuc]
);

  const anySelected = useMemo(() => {
    return Boolean(
      frontPhoto ||
        backPhoto ||
        rcFile ||
        insuranceFile ||
        permitFile ||
        fitnessFile ||
        pucFile
    );
  }, [frontPhoto, backPhoto, rcFile, insuranceFile, permitFile, fitnessFile, pucFile]);

  const selectedCount = useMemo(() => {
    return [
      frontPhoto,
      backPhoto,
      rcFile,
      insuranceFile,
      permitFile,
      fitnessFile,
      pucFile,
    ].filter(Boolean).length;
  }, [frontPhoto, backPhoto, rcFile, insuranceFile, permitFile, fitnessFile, pucFile]);

  const refreshCompliance = useCallback(async () => {
    if (!hasReg) return normalizeCompliance(null);
    const raw = await fetchVehicleCompliance(registrationNumber);
    const norm = normalizeCompliance(raw);
    setCompliance(norm);
    return norm;
  }, [hasReg, registrationNumber]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!hasReg) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const raw = await fetchVehicleCompliance(registrationNumber);
        if (!active) return;
        setCompliance(normalizeCompliance(raw));
      } catch (err) {
        console.debug("Initial compliance load failed:", err?.message || err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [hasReg, registrationNumber]);

  const patchComplianceDocSaved = useCallback((type, file) => {
    setCompliance((prev) => {
      const base = prev || normalizeCompliance(null);
      return {
        ...base,
        docs: {
          ...(base.docs || {}),
          [type]: {
            ...(base.docs?.[type] || {}),
            docType: type,
            fileUrl: base.docs?.[type]?.fileUrl || `local:${type}:${Date.now()}`,
            filename: file?.name || base.docs?.[type]?.filename || "",
          },
        },
      };
    });
  }, []);

  const patchCompliancePhotoSaved = useCallback((type, file) => {
    setCompliance((prev) => {
      const base = prev || normalizeCompliance(null);
      return {
        ...base,
        photos: {
          ...(base.photos || {}),
          [type]: {
            ...(base.photos?.[type] || {}),
            photoType: type,
            fileUrl: base.photos?.[type]?.fileUrl || `local:${type}:${Date.now()}`,
            filename: file?.name || base.photos?.[type]?.filename || "",
          },
        },
      };
    });
  }, []);

  const handleSingleChange = useCallback((setter) => {
    return (e) => {
      const file = e?.target?.files?.[0] || null;
      setter(file);
      e.target.value = "";
    };
  }, []);

  const openViewer = useCallback(async (title, fileUrl) => {
    if (!fileUrl) return;

    try {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }

      setViewer({
        open: true,
        busy: true,
        title,
        objectUrl: "",
        fileKind: "other",
        fileUrl,
        filename: "",
      });

      const res = await fetchVehicleFileForPreview(fileUrl);
      if (!res?.blob) {
        throw new Error("Preview unavailable");
      }

      const fileKind = guessKindFromMimeOrName(
        res.contentType,
        res.filename || fileUrl
      );
      const objectUrl = URL.createObjectURL(
        res.contentType ? new Blob([res.blob], { type: res.contentType }) : res.blob
      );

      previewUrlRef.current = objectUrl;

      setViewer({
        open: true,
        busy: false,
        title,
        objectUrl,
        fileKind,
        fileUrl,
        filename: res.filename || "",
      });
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setViewer((prev) => ({
        ...prev,
        open: true,
        busy: false,
        title,
        objectUrl: "",
        fileKind: "other",
        fileUrl,
        filename: "",
      }));
    }
  }, []);

  const closeViewer = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setViewer({
      open: false,
      busy: false,
      title: "",
      objectUrl: "",
      fileKind: "other",
      fileUrl: "",
      filename: "",
    });
  }, []);

  const handleViewerDownload = useCallback(async () => {
    if (!viewer?.fileUrl) return;
    try {
      await downloadVehicleFile(viewer.fileUrl, viewer.filename || undefined);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, [viewer]);

  const handleSave = async () => {
    if (!hasReg) {
      toast.error("Registration number is missing for this vehicle.");
      return;
    }

    if (!anySelected) {
      toast.info("Select at least one file to upload.");
      return;
    }

    setSubmitting(true);

    const tasks = [];

    const pushPhotoTask = (type, file) => {
      if (!file) return;
      tasks.push(async () => {
        await uploadVehiclePhoto(registrationNumber, file, type);
        patchCompliancePhotoSaved(type, file);
        return { ok: true, kind: "photo", type };
      });
    };

    const pushDocTask = (type, file) => {
      if (!file) return;
      tasks.push(async () => {
        await uploadVehicleDocument(registrationNumber, file, type);
        patchComplianceDocSaved(type, file);
        return { ok: true, kind: "doc", type };
      });
    };

    pushPhotoTask(PHOTO_SLOTS.front, frontPhoto);
    pushPhotoTask(PHOTO_SLOTS.back, backPhoto);

    pushDocTask(DOC_TYPES.rc, rcFile);
    pushDocTask(DOC_TYPES.insurance, insuranceFile);
    if (requiresPermit) pushDocTask(DOC_TYPES.permit, permitFile);
    if (requiresFitness) pushDocTask(DOC_TYPES.fitness, fitnessFile);
    if (requiresPuc) pushDocTask(DOC_TYPES.puc, pucFile);

    try {
      const results = await runLimited(tasks, 3);

      const failures = results.filter((r) => r?.status === "rejected");
      const successes = results.filter((r) => r?.status === "fulfilled");

      if (successes.length > 0) {
        const refreshed = await refreshCompliance();

        if (canTriggerVerification(refreshed, { requiresPermit, requiresFitness, requiresPuc })) {
          try {
            await verifyVehicleFromRegistry(registrationNumber);
          } catch (err) {
            console.debug("Registry verification skipped/failed:", err?.message || err);
          }
          await refreshCompliance();
        }

        setFrontPhoto(null);
        setBackPhoto(null);
        setRcFile(null);
        setInsuranceFile(null);
        setPermitFile(null);
        setFitnessFile(null);
        setPucFile(null);

        toast.success(
          failures.length === 0
            ? "Files saved successfully."
            : `${successes.length} file(s) saved. Some files could not be uploaded.`
        );
      }

      if (successes.length === 0 && failures.length > 0) {
        toast.error(extractErrorMessage(failures[0].reason));
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate("/owner/manage-cars");

  const primaryActionLabel = submitting
    ? "Saving..."
    : anySelected
    ? `Save ${selectedCount} file${selectedCount > 1 ? "s" : ""}`
    : "Select files to save";

  const overall = getOverallComplianceMeta(compliance);

  const summaryInfoChips = useMemo(() => {
    const raw = compliance?.raw || {};
    const chips = [];

    if (raw?.engineNumber) chips.push(`Engine ${raw.engineNumber}`);
    if (raw?.chassisNumber) chips.push(`Chassis ${raw.chassisNumber}`);
    if (raw?.verifiedAt) chips.push(`Verified ${fmtDate(raw.verifiedAt)}`);
    if (compliance?.updatedAt) chips.push(`Updated ${fmtDate(compliance.updatedAt)}`);

    return chips;
  }, [compliance]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      <main className="w-full px-4 pb-40 pt-5 sm:px-6 sm:pt-6 lg:px-8 xl:px-10">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="border-b border-slate-200 pb-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Vehicle documents
                </div>

                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 sm:text-[34px]">
                  Documents & photos
                </h1>

                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-600">
                  {registrationNumber ? <MutedChip>{registrationNumber}</MutedChip> : null}
                  {vehicle?.brand || vehicle?.model ? (
                    <MutedChip>
                      {[vehicle?.brand, vehicle?.model].filter(Boolean).join(" ")}
                    </MutedChip>
                  ) : null}
                  {purchaseYear ? <MutedChip>Year {purchaseYear}</MutedChip> : null}
                  {!isIndependentNonCar && !requiresPuc ? <MutedChip>PUC optional</MutedChip> : null}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <ActionButton variant="secondary" onClick={handleBack}>
                  Back
                </ActionButton>
              </div>
            </div>
          </div>

          {!loading ? (
            <div className="mt-6 grid gap-4 xl:grid-cols-[1.3fr_0.7fr]">
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 sm:px-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Chip tone={overall.tone}>{overall.label}</Chip>
                    <div className="mt-2 text-sm text-slate-600">{overall.hint}</div>
                  </div>

                  {summaryInfoChips.length > 0 ? (
                    <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
                      {summaryInfoChips.map((chip, idx) => (
                        <MutedChip key={`summary-${idx}`}>{chip}</MutedChip>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 sm:px-6">
                <div className="text-sm font-semibold text-slate-950">Reupload policy</div>
                <div className="mt-2 text-sm text-slate-600">
                  Only items pending, failed, expiring soon, expired, or explicitly reopened can be replaced.
                </div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white px-6 py-20 text-center text-sm text-slate-600">
              Loading files…
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <SimpleSection
                title="Vehicle photos"
                description="Add clear exterior photos with number plate visibility."
              >
                {photoRows.map((row, index) => {
                  const item = serverPhoto(row.type);

                  return (
                    <React.Fragment key={row.key}>
                      {index > 0 ? <ListDivider /> : null}
                      <DocumentRow
                        title={row.title}
                        hint={row.hint}
                        accept=".jpg,.jpeg,.png,.webp,image/*"
                        disabled={submitting}
                        selectedFile={row.selectedFile}
                        existingItem={item}
                        onPick={handleSingleChange(row.setSelectedFile)}
                        onClear={() => row.setSelectedFile(null)}
                        onView={() => openViewer(row.title, item?.fileUrl)}
                        uploadText="Upload"
                        isPhoto
                      />
                    </React.Fragment>
                  );
                })}
              </SimpleSection>

              <SimpleSection
                title="Vehicle documents"
                description="Upload the latest valid documents for verification."
              >
                {docRows.map((row, index) => {
                  const item = serverDoc(row.type);

                  return (
                    <React.Fragment key={row.key}>
                      {index > 0 ? <ListDivider /> : null}
                      <DocumentRow
                        title={row.title}
                        hint={row.hint}
                        accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                        disabled={submitting}
                        selectedFile={row.selectedFile}
                        existingItem={item}
                        onPick={handleSingleChange(row.setSelectedFile)}
                        onClear={() => row.setSelectedFile(null)}
                        onView={() => openViewer(row.title, item?.fileUrl)}
                        uploadText="Upload"
                      />
                    </React.Fragment>
                  );
                })}
              </SimpleSection>
            </div>
          )}
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-3 px-4 py-4 sm:px-6 lg:px-8 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-950">
              {anySelected
                ? `${selectedCount} file${selectedCount > 1 ? "s" : ""} selected`
                : "No new files selected"}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Save only the items you changed.
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <ActionButton variant="secondary" onClick={handleBack} disabled={submitting}>
              Back
            </ActionButton>
            <ActionButton
              variant="primary"
              onClick={handleSave}
              disabled={submitting || !anySelected}
              className="w-full sm:min-w-[220px] sm:w-auto"
            >
              {primaryActionLabel}
            </ActionButton>
          </div>
        </div>
      </div>

      <Footer />

      <DocumentViewerModal
        open={viewer.open}
        onClose={closeViewer}
        title={viewer.title}
        objectUrl={viewer.objectUrl}
        fileKind={viewer.fileKind}
        busy={viewer.busy}
        onDownload={handleViewerDownload}
      />
    </div>
  );
}
